import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSearchParams, Link } from "react-router-dom";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserPlus, CheckCircle2, Ban, BadgeCheck, Pencil, FolderUp, Upload, Undo2, Trash2, MailWarning, FileWarning, Wallet, ScanLine, Download } from "lucide-react";
import UserNameLink from "@/components/UserNameLink";
import FreelancesAtRiskBlock from "@/components/FreelancesAtRiskBlock";
import CompensationDialog from "@/components/CompensationDialog";
import { useAuth } from "@/lib/auth";

const ROLES = ["COLLABORATOR", "MANAGER", "GESTIONNAIRE", "ADMIN", "CEO"];
const ROLE_OPTIONS = [
  { value: "COLLABORATOR_INTERNAL", label: "Collaborateur Interne", role: "COLLABORATOR", is_freelance: false },
  { value: "COLLABORATOR_FREELANCE", label: "Collaborateur Freelance", role: "COLLABORATOR", is_freelance: true },
  { value: "MANAGER", label: "Manager", role: "MANAGER", is_freelance: false },
  // Iter-191f — a freelance who is also authorized to manage missions.
  // Billed/paid as a freelance, but holds MANAGER permissions on missions assigned to them.
  { value: "MANAGER_FREELANCE", label: "Manager Freelance", role: "MANAGER", is_freelance: true },
  { value: "GESTIONNAIRE", label: "Gestionnaire", role: "GESTIONNAIRE", is_freelance: false },
  { value: "ADMIN", label: "Admin", role: "ADMIN", is_freelance: false },
  { value: "CEO", label: "CEO", role: "CEO", is_freelance: false },
];
// Iter-40/41: which row roles ADMIN may modify (COLLABORATOR_INTERNAL, ADMIN). Freelance and
// elevated roles are read-only for them; the Select renders disabled with the current role
// label so the admin still sees who's who.
const ADMIN_EDITABLE_TARGET_ROLES = new Set(["COLLABORATOR", "ADMIN"]);
const ADMIN_NEW_ROLE_OPTIONS = new Set(["COLLABORATOR_INTERNAL", "ADMIN"]);
const FREELANCE_CURRENCIES = ["MAD", "EUR", "USD", "XOF"];

function userToRoleOption(u) {
  if (u.role === "COLLABORATOR") return u.is_freelance ? "COLLABORATOR_FREELANCE" : "COLLABORATOR_INTERNAL";
  // Iter-191f — a MANAGER who is also a freelance maps to the new combined option.
  if (u.role === "MANAGER" && u.is_freelance) return "MANAGER_FREELANCE";
  return u.role;
}

export default function AdminUsers({ freelancesOnly = false }) {
  const { user: currentUser } = useAuth();
  const isAdminOnly = currentUser?.role === "ADMIN";
  const isGestionnaire = currentUser?.role === "GESTIONNAIRE";
  const adminCanEditRow = (u) => {
    // Iter-44: gestionnaire can edit ANY freelance profile (it's the whole point of their
    // dedicated /admin/freelancers menu). Other internal roles remain read-only for them.
    if (isGestionnaire) return !!u.is_freelance;
    if (!isAdminOnly) return true;
    if (u.user_id === currentUser?.user_id) return false; // can't edit self
    if (u.role === "ADMIN") return false; // can't edit other admins
    if (!ADMIN_EDITABLE_TARGET_ROLES.has(u.role)) return false; // CEO/MANAGER/GESTIONNAIRE = read-only
    if (u.is_freelance) return false; // freelance profiles are CEO-only
    return true;
  };
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [compensationTarget, setCompensationTarget] = useState(null);
  const canManageCompensation = currentUser?.role === "CEO" || currentUser?.role === "GESTIONNAIRE";
  const [tab, setTab] = useState("active");
  const [form, setForm] = useState({ email: "", name: "", role: "COLLABORATOR" });
  const [portageCompanies, setPortageCompanies] = useState([]);
  const [globalInvoiceDriveUrl, setGlobalInvoiceDriveUrl] = useState("");
  const [freelanceTarget, setFreelanceTarget] = useState(null);
  const [flForm, setFlForm] = useState(null);
  const [flUploading, setFlUploading] = useState(false);
  const [editInvoiceUrl, setEditInvoiceUrl] = useState(false); // toggle when CEO wants to override the global default
  const [newPortageOpen, setNewPortageOpen] = useState(false);
  const [newPortage, setNewPortage] = useState({ name: "", address: "", phone: "", email: "", ice: "", bank_name: "", swift: "", iban: "", domiciliation: "" });
  const [newPortageBankingOpen, setNewPortageBankingOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [missingContract, setMissingContract] = useState([]);
  const [reminding, setReminding] = useState(false);
  // Iter-44: when arriving from the Last-24h pulse banner, filter the users list to those
  // whose audit-log row matches the requested action over the last 24h.
  const last24hFilter = searchParams.get("last24h");
  const [last24hUserIds, setLast24hUserIds] = useState(null);
  useEffect(() => {
    if (!last24hFilter) { setLast24hUserIds(null); return; }
    const action = {
      approved: "USER_APPROVED",
      suspended: "USER_SUSPENDED",
      unsuspended: "USER_UNSUSPENDED",
      role_changed: "USER_ROLE_CHANGED",
      deleted: "USER_DELETED",
    }[last24hFilter];
    if (!action) { setLast24hUserIds(null); return; }
    api.get(`/admin/audit-log?action=${action}&since_hours=24`)
      .then((r) => setLast24hUserIds(new Set((r.data || []).map((row) => row.target_user_id))))
      .catch(() => setLast24hUserIds(new Set()));
  }, [last24hFilter]);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  const loadMissingContract = () => api.get("/freelance/admin/missing-contract")
    .then((r) => setMissingContract(r.data || []))
    .catch(() => setMissingContract([]));
  useEffect(() => {
    load();
    loadMissingContract();
    api.get("/portage-companies").then((r) => setPortageCompanies(r.data)).catch(() => {});
    api.get("/settings").then((r) => setGlobalInvoiceDriveUrl(r.data?.freelance_invoice_drive_folder_url || "")).catch(() => {});
  }, []);

  const remindMissingContract = async () => {
    setReminding(true);
    try {
      const r = await api.post("/freelance/admin/missing-contract/remind");
      toast.success(`Rappel sent (${r.data.sent} recipient(s))`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setReminding(false); }
  };

  // Auto-open the freelance config dialog when redirected here with ?openFreelance=<user_id>
  // (used by WorkspaceDirectory after a Freelance provisioning so the CEO finalises the contract).
  useEffect(() => {
    const target = searchParams.get("openFreelance");
    if (!target || users.length === 0) return;
    const u = users.find((x) => x.user_id === target);
    if (u) {
      openFreelanceConfig(u);
      // Strip the query param to avoid re-opening on every render
      const next = new URLSearchParams(searchParams);
      next.delete("openFreelance");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchParams]);

  const change = async (user_id, optValue) => {
    const opt = ROLE_OPTIONS.find((o) => o.value === optValue);
    if (!opt) return;
    const u = users.find((x) => x.user_id === user_id);
    try {
      // Update role first if it changed
      if (u && u.role !== opt.role) {
        await api.patch(`/users/${user_id}/role`, { role: opt.role });
      }
      // Iter-191f — freelance flag toggling is now allowed for both COLLABORATOR and MANAGER.
      const targetRoleSupportsFreelance = opt.role === "COLLABORATOR" || opt.role === "MANAGER";
      if (targetRoleSupportsFreelance) {
        if (opt.is_freelance && !u?.is_freelance) {
          // Open the freelance config dialog so the CEO fills the contract details
          toast.info("Configure the freelance profile to finalize");
          await load();
          const fresh = (await api.get("/users")).data.find((x) => x.user_id === user_id);
          if (fresh) openFreelanceConfig(fresh);
          return;
        }
        if (!opt.is_freelance && u?.is_freelance) {
          if (!window.confirm("Disable this user's freelance profile?")) {
            await load();
            return;
          }
          await api.delete(`/users/${user_id}/freelance`);
        }
      } else if (u?.is_freelance) {
        // Switching role away from COLLABORATOR/MANAGER — drop the freelance profile
        await api.delete(`/users/${user_id}/freelance`).catch(() => {});
      }
      toast.success("Profile updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const approve = async (u, role) => {
    try {
      await api.post(`/users/${u.user_id}/approve`, { role: role || null });
      toast.success(`${u.name} approved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const suspend = async (u) => {
    if (!window.confirm(`Suspend ${u.name}? They won't be able to use the app.`)) return;
    try {
      await api.post(`/users/${u.user_id}/suspend`);
      toast.success("Suspended");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const unsuspend = async (u) => {
    if (!window.confirm(`Reactivate ${u.name}? They will regain full access.`)) return;
    try {
      await api.post(`/users/${u.user_id}/unsuspend`);
      toast.success("Reactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  // Iter-231 — full data export (ZIP of CSVs + JSON), CEO/ADMIN only.
  const [exportingId, setExportingId] = useState(null);
  const exportUser = async (u) => {
    setExportingId(u.user_id);
    try {
      const r = await api.get(`/users/${u.user_id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BAIM_Export_${(u.name || "user").replace(/\s+/g, "-")}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Data export for ${u.name} downloaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed"); }
    finally { setExportingId(null); }
  };

  // Iter-231 — permanent deletion requires typing the user's name (strong confirm).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const deleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const r = await api.delete(`/users/${deleteTarget.user_id}`);
      const arch = r.data?.archive || {};
      if (arch.drive_web_view_link) {
        toast.success(`${deleteTarget.name} deleted — export archived to Drive`);
      } else {
        toast.success(`${deleteTarget.name} permanently deleted`);
      }
      setDeleteTarget(null);
      setDeleteConfirmName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setDeleting(false); }
  };

  const addUser = async () => {
    try {
      await api.post("/users", form);
      toast.success("User added");
      setDialog(false); setForm({ email: "", name: "", role: "COLLABORATOR" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openFreelanceConfig = (u) => {
    const fp = u.freelance_profile || {};
    setFreelanceTarget(u);
    setFlForm({
      tjm_ht: fp.tjm_ht ?? "",
      currency: fp.currency || "MAD",
      contract_start: fp.contract_start || "",
      contract_end: fp.contract_end || "",
      portage_company_id: fp.portage_company_id || (portageCompanies[0]?.company_id || ""),
      drive_folder_url: fp.drive_folder_url || "",
      // Per-user invoice drive override; defaults to the global setting and stays null on the wire when not overridden.
      invoice_drive_folder_url: fp.invoice_drive_folder_url || "",
    });
    setEditInvoiceUrl(false);
  };

  const saveFreelance = async () => {
    if (!flForm.portage_company_id) return toast.error("Select a portage company");
    if (!(parseFloat(flForm.tjm_ht) > 0)) return toast.error("TJM HT invalide");
    if (!flForm.contract_start || !flForm.contract_end) return toast.error("Dates de contrat requises");
    if (!flForm.drive_folder_url) return toast.error("URL Drive — Documents administratifs requise");
    try {
      const payload = {
        ...flForm,
        tjm_ht: parseFloat(flForm.tjm_ht),
        // Send the override only when the CEO explicitly edits it; otherwise null lets backend fall back to the global setting.
        invoice_drive_folder_url: editInvoiceUrl ? (flForm.invoice_drive_folder_url || null) : null,
      };
      await api.post(`/users/${freelanceTarget.user_id}/freelance`, payload);
      toast.success("Freelance profile saved");
      setFreelanceTarget(null); setFlForm(null); setEditInvoiceUrl(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const removeFreelance = async () => {
    if (!window.confirm(`Désactiver le profil freelance de ${freelanceTarget.name} ?`)) return;
    try {
      await api.delete(`/users/${freelanceTarget.user_id}/freelance`);
      toast.success("Freelance profile removed");
      setFreelanceTarget(null); setFlForm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const uploadFreelanceContract = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("PDF uniquement");
    setFlUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/users/${freelanceTarget.user_id}/freelance/contract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Contract uploaded to shared Drive");
      // Refresh user list to get the new contract URL
      const r = await api.get("/users");
      setUsers(r.data);
      const fresh = r.data.find((u) => u.user_id === freelanceTarget.user_id);
      if (fresh) setFreelanceTarget(fresh);
      loadMissingContract();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error upload"); }
    finally { setFlUploading(false); }
  };

  const createPortageInline = async () => {
    if (!newPortage.name.trim()) return toast.error("Name required");
    try {
      const r = await api.post("/portage-companies", newPortage);
      toast.success("Client created");
      const fresh = await api.get("/portage-companies");
      setPortageCompanies(fresh.data);
      setFlForm((prev) => prev ? { ...prev, portage_company_id: r.data.company_id } : prev);
      setNewPortageOpen(false);
      setNewPortage({ name: "", address: "", phone: "", email: "", ice: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const pending = users.filter((u) => u.status === "PENDING");
  const active = users.filter((u) => u.status !== "PENDING");
  // Iter-44: when last24h filter is active, restrict displayed users to those whose audit-log
  // row matches. The Set is built async; while loading we still show the unfiltered list.
  let visibleActive = last24hUserIds ? active.filter((u) => last24hUserIds.has(u.user_id)) : active;
  let visiblePending = last24hUserIds ? pending.filter((u) => last24hUserIds.has(u.user_id)) : pending;
  // Iter-44: dedicated /admin/freelancers page → only freelance users.
  if (freelancesOnly) {
    visibleActive = visibleActive.filter((u) => !!u.is_freelance);
    visiblePending = visiblePending.filter((u) => !!u.is_freelance);
  }
  const rows = tab === "pending" ? visiblePending : visibleActive;

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      {/* Iter-44: dedicated freelancers page — show the at-risk block at the very top. */}
      {freelancesOnly && (
        <div className="mb-8" data-testid="admin-freelancers-at-risk-wrap">
          <FreelancesAtRiskBlock />
        </div>
      )}
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">Admin{freelancesOnly ? " · Freelancers" : ""}</div>
          <h1 className="font-heading text-2xl md:text-4xl font-bold tracking-tight">{freelancesOnly ? "Freelancers" : "Users & roles"}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl">{freelancesOnly ? "Manage every freelance profile: portage company, TJM, signed contract." : "Manage which users have access and which role they hold inside BAIM Mission Control."}</p>
        </div>
        <Dialog open={dialog} onOpenChange={setDialog}>
          {currentUser?.role === "CEO" && (
            <DialogTrigger asChild>
              <button data-testid="add-user-btn" className="inline-flex items-center gap-2 px-5 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800">
                <UserPlus size={14} /> Add user
              </button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>Add a user</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Email</Label><Input data-testid="adduser-email" placeholder="name@baim.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>Name</Label><Input data-testid="adduser-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="adduser-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(isAdminOnly ? ["COLLABORATOR", "ADMIN"] : ROLES).map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialog(false)} className="px-4 h-10 text-sm hover:bg-zinc-100 rounded-md">Cancel</button>
              <button type="button" data-testid="adduser-save" onClick={addUser} className="px-4 h-10 bg-zinc-950 text-white text-sm rounded-md">Add</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Iter-44: filter banner when arriving from Last 24h pulse */}
      {last24hFilter && (
        <div className="mb-6 bg-brand-navy/95 text-white rounded-lg p-4 flex items-center justify-between gap-4" data-testid="last24h-filter-banner">
          <div className="text-sm">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60 mr-2">Filter</span>
            Showing users with <strong className="capitalize">{last24hFilter.replace("_", " ")}</strong> activity in the last 24 hours
            {last24hUserIds && <span className="ml-2 font-mono text-white/60">({last24hUserIds.size} match)</span>}
          </div>
          <button
            data-testid="last24h-filter-clear"
            onClick={() => { const next = new URLSearchParams(searchParams); next.delete("last24h"); setSearchParams(next, { replace: true }); }}
            className="text-xs underline hover:text-brand-orange-200"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Banner: freelances missing contract — hidden for ADMIN (no action rights on freelances since iter-41). */}
      {missingContract.length > 0 && !isAdminOnly && (
        <div className="mb-6 bg-red-50 border border-red-300 rounded-lg p-5" data-testid="missing-contract-banner">
          <div className="flex items-start gap-3 mb-3">
            <FileWarning size={20} className="text-red-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-red-900">
                {missingContract.length} freelance(s) sans contrat uploaded
              </div>
              <div className="text-xs text-red-900/80 mt-1">
                Téléchargez le PDF signé depuis chaque profil ci-dessous (icône <FolderUp size={11} className="inline" />) ou click « Relancer » pour send un rappel par email à tous les CEO + Gestionnaires.
              </div>
            </div>
            <button
              onClick={remindMissingContract}
              disabled={reminding}
              data-testid="missing-contract-remind"
              className="inline-flex items-center gap-1.5 px-3 h-9 bg-red-700 text-white text-xs rounded hover:bg-red-800 disabled:opacity-50 shrink-0"
            >
              <MailWarning size={12} /> {reminding ? "Sending…" : "Send reminder now"}
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {missingContract.map((m) => (
              <span key={m.user_id} data-testid={`missing-contract-chip-${m.user_id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 bg-white/70 border border-red-200 rounded text-red-900">
                <BadgeCheck size={10} /> {m.name} <span className="text-zinc-500 font-mono">({m.portage_company_name || "?"})</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-zinc-200">
        {[
          { key: "active", label: `Active (${active.length})` },
          { key: "pending", label: `Pending approval (${pending.length})` },
        ].map((t) => (
          <button key={t.key} data-testid={`admin-tab-${t.key}`} onClick={() => setTab(t.key)} className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === t.key ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg md:overflow-x-auto md:overflow-hidden">
        <div className="hidden md:grid md:grid-cols-12 md:min-w-[760px] px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-zinc-200 font-mono">
          <div className="col-span-4">Name</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-4 text-right">{tab === "pending" ? "Actions" : "Role / Status"}</div>
        </div>
        {rows.map((u) => (
          <div key={u.user_id} className={`flex flex-col gap-3 md:grid md:grid-cols-12 md:items-center md:min-w-[760px] md:gap-0 px-5 py-4 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-50 ${u.status === "SUSPENDED" ? "opacity-60 bg-zinc-50/70 grayscale" : ""}`} data-testid={`user-row-${u.user_id}`}>
            <div className="md:col-span-4 flex items-center gap-3 min-w-0">
              {u.picture ? (
                <img src={u.picture} className="w-8 h-8 rounded-full object-cover" alt="" referrerPolicy="no-referrer" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-xs font-medium uppercase">{u.name?.slice(0, 2) || "U"}</div>
              )}
              <div className="font-medium truncate">
                <UserNameLink user={u} testid={`adminusers-name-link-${u.user_id}`} />
              </div>
            </div>
            <div className="md:col-span-4 text-sm text-zinc-700 truncate font-mono">{u.email}</div>
            <div className="md:col-span-4 flex items-center md:justify-end gap-2 flex-wrap">
              {tab === "pending" ? (
                <>
                  <Select defaultValue="COLLABORATOR" onValueChange={(v) => u._pendingRole = v}>
                    <SelectTrigger className="w-36" data-testid={`pending-role-${u.user_id}`}><SelectValue placeholder="Role on approve" /></SelectTrigger>
                    <SelectContent>
                      {(isAdminOnly ? ["COLLABORATOR", "ADMIN"] : ROLES).map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button onClick={() => approve(u, u._pendingRole || "COLLABORATOR")} data-testid={`approve-btn-${u.user_id}`} className="inline-flex items-center gap-1 text-xs px-3 h-9 bg-zinc-950 text-white rounded hover:bg-zinc-800">
                    <CheckCircle2 size={13} /> Approve
                  </button>
                </>
              ) : (
                <>
                  {u.status === "SUSPENDED" && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded font-mono">SUSPENDED</span>}
                  {u.is_freelance && (
                    <span data-testid={`freelance-badge-${u.user_id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-brand-orange-100 text-brand-orange-600 rounded font-mono uppercase">
                      <BadgeCheck size={10} /> Freelance
                    </span>
                  )}
                  <Select
                    value={userToRoleOption(u)}
                    onValueChange={(v) => change(u.user_id, v)}
                    disabled={!adminCanEditRow(u)}
                  >
                    <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLE_OPTIONS.filter((o) => !isAdminOnly || ADMIN_NEW_ROLE_OPTIONS.has(o.value) || o.value === userToRoleOption(u)).map((o) => (
                        <SelectItem key={o.value} value={o.value} data-testid={`role-opt-${o.value}-${u.user_id}`}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Iter-194t — open the freelance config button to BOTH
                      Collaborateur Freelance AND Manager Freelance. Previously
                      the test `u.role === "COLLABORATOR"` hid the button for
                      MANAGER_FREELANCE which left CEO unable to edit their
                      freelance rates / IBAN / currency. We now show it iff
                      the user is already configured as freelance OR is on a
                      COLLABORATOR role (so they can be promoted to freelance). */}
                  {((u.is_freelance && (u.role === "COLLABORATOR" || u.role === "MANAGER")) || (u.role === "COLLABORATOR" && !u.is_freelance)) && !isAdminOnly && (
                    <button
                      onClick={() => openFreelanceConfig(u)}
                      data-testid={`freelance-config-${u.user_id}`}
                      title={u.is_freelance ? "Edit freelance profile" : "Configurer en freelance"}
                      className={`p-2 rounded ${u.is_freelance ? "text-brand-orange-600 hover:bg-brand-orange-100" : "text-zinc-400 hover:text-brand-navy hover:bg-brand-cream-200"}`}
                    >
                      <BadgeCheck size={14} />
                    </button>
                  )}
                  {/* Iter-193g — quick access to user's expense-notes history (CEO/ADMIN/GESTIONNAIRE). */}
                  <Link
                    to={`/admin/users/${u.user_id}/expenses`}
                    data-testid={`user-expenses-${u.user_id}`}
                    title="Historique notes de frais"
                    className="p-2 rounded text-zinc-400 hover:text-brand-navy hover:bg-brand-cream-200"
                  >
                    <ScanLine size={14} />
                  </Link>
                  {/* Iter-231 — full data export (ZIP), for ANY user, CEO/ADMIN only. */}
                  {(currentUser?.role === "CEO" || currentUser?.role === "ADMIN") && (
                    <button
                      onClick={() => exportUser(u)}
                      disabled={exportingId === u.user_id}
                      data-testid={`export-btn-${u.user_id}`}
                      title="Export all user data (ZIP: CSV + JSON)"
                      className="p-2 text-zinc-400 hover:text-brand-navy hover:bg-brand-cream-200 rounded disabled:opacity-50"
                    >
                      <Download size={14} className={exportingId === u.user_id ? "animate-pulse" : ""} />
                    </button>
                  )}
                  {u.role !== "CEO" && u.status !== "SUSPENDED" && (
                    <button onClick={() => suspend(u)} data-testid={`suspend-btn-${u.user_id}`} title="Suspend" className="p-2 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"><Ban size={14} /></button>
                  )}
                  {canManageCompensation && u.status !== "SUSPENDED" && (
                    <button
                      onClick={() => setCompensationTarget(u)}
                      data-testid={`compensation-btn-${u.user_id}`}
                      title="Rémunération / CJM"
                      className="p-2 text-zinc-400 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                    >
                      <Wallet size={14} />
                    </button>
                  )}
                  {u.status === "SUSPENDED" && (
                    <>
                      {/* Iter-231 — reactivation is CEO-only; ADMIN sees a disabled button. */}
                      {currentUser?.role === "CEO" ? (
                        <button onClick={() => unsuspend(u)} data-testid={`unsuspend-btn-${u.user_id}`} title="Reactivate (CEO validation)" className="p-2 text-emerald-600 hover:bg-emerald-50 rounded"><Undo2 size={14} /></button>
                      ) : (
                        <button disabled data-testid={`unsuspend-btn-disabled-${u.user_id}`} title="Reactivation requires CEO validation" className="p-2 text-zinc-300 cursor-not-allowed rounded"><Undo2 size={14} /></button>
                      )}
                      <button onClick={() => { setDeleteTarget(u); setDeleteConfirmName(""); }} data-testid={`delete-btn-${u.user_id}`} title="Delete permanently" className="p-2 text-red-600 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="px-5 py-16 text-sm text-zinc-500 text-center">
            {tab === "pending" ? "No users awaiting approval." : "No users."}
          </div>
        )}
      </div>

      {/* Freelance config dialog */}
      <Dialog open={!!freelanceTarget} onOpenChange={(o) => { if (!o) { setFreelanceTarget(null); setFlForm(null); setEditInvoiceUrl(false); } }}>
        <DialogContent className="max-w-xl" data-testid="freelance-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck size={18} className="text-brand-orange" />
              {freelanceTarget?.is_freelance ? "Edit" : "Configurer"} le profil freelance — {freelanceTarget?.name}
            </DialogTitle>
          </DialogHeader>
          {flForm && (
            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>TJM HT *</Label>
                  <Input type="number" min="0" step="any" value={flForm.tjm_ht} onChange={(e) => setFlForm({ ...flForm, tjm_ht: e.target.value })} data-testid="fl-tjm" />
                </div>
                <div>
                  <Label>Devise</Label>
                  <Select value={flForm.currency} onValueChange={(v) => setFlForm({ ...flForm, currency: v })}>
                    <SelectTrigger data-testid="fl-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FREELANCE_CURRENCIES.map((c) => <SelectItem key={c} value={c} data-testid={`fl-currency-opt-${c}`}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Contract start *</Label>
                  <Input type="date" value={flForm.contract_start} onChange={(e) => setFlForm({ ...flForm, contract_start: e.target.value })} data-testid="fl-start" />
                </div>
                <div>
                  <Label>Fin de contrat *</Label>
                  <Input type="date" value={flForm.contract_end} onChange={(e) => setFlForm({ ...flForm, contract_end: e.target.value })} data-testid="fl-end" />
                </div>
              </div>
              <div>
                <Label>Company de portage *</Label>
                <div className="flex items-stretch gap-2">
                  <Select value={flForm.portage_company_id} onValueChange={(v) => setFlForm({ ...flForm, portage_company_id: v })}>
                    <SelectTrigger data-testid="fl-portage" className="flex-1">
                      <SelectValue placeholder={portageCompanies.length === 0 ? "No company — add one" : "Choisir un cabinet"} />
                    </SelectTrigger>
                    <SelectContent>
                      {portageCompanies.map((c) => <SelectItem key={c.company_id} value={c.company_id} data-testid={`fl-portage-opt-${c.company_id}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={() => setNewPortageOpen(true)}
                    data-testid="fl-portage-add-new"
                    className="inline-flex items-center gap-1 px-3 h-10 border border-brand-navy/30 rounded-md text-xs text-brand-navy hover:bg-brand-cream-200"
                  >
                    + Nouveau
                  </button>
                </div>
              </div>
              <div>
                <Label>Lien Google Drive — Documents administratifs (contrats, NDA…) *</Label>
                <Input type="url" placeholder="https://drive.google.com/drive/folders/..." value={flForm.drive_folder_url} onChange={(e) => setFlForm({ ...flForm, drive_folder_url: e.target.value })} data-testid="fl-drive-url" />
                <p className="text-[11px] text-zinc-500 mt-1">Private folder for this freelance — admin documents only (signed contract, NDA…). The freelance has no access.</p>
              </div>

              <div data-testid="fl-invoice-drive-block">
                <div className="flex items-center justify-between mb-2">
                  <Label className="m-0">Lien Google Drive — Factures freelance</Label>
                  <button
                    type="button"
                    onClick={() => {
                      if (editInvoiceUrl) {
                        // Reverting to default — clear the per-user override
                        setFlForm({ ...flForm, invoice_drive_folder_url: "" });
                      }
                      setEditInvoiceUrl((v) => !v);
                    }}
                    data-testid="fl-invoice-drive-edit-toggle"
                    className="inline-flex items-center gap-1 text-[11px] text-brand-navy hover:underline"
                  >
                    <Pencil size={11} />
                    {editInvoiceUrl ? "Revert to global default" : "Edit"}
                  </button>
                </div>
                <Input
                  type="url"
                  placeholder="https://drive.google.com/drive/folders/..."
                  value={editInvoiceUrl ? (flForm.invoice_drive_folder_url || "") : (flForm.invoice_drive_folder_url || globalInvoiceDriveUrl)}
                  onChange={(e) => setFlForm({ ...flForm, invoice_drive_folder_url: e.target.value })}
                  disabled={!editInvoiceUrl}
                  data-testid="fl-invoice-drive-url"
                  className={!editInvoiceUrl ? "bg-zinc-50 text-zinc-600" : ""}
                />
                <p className="text-[11px] text-zinc-500 mt-1">
                  {editInvoiceUrl
                    ? <>Override for this freelance only. Leave empty to revert to the global default.</>
                    : flForm.invoice_drive_folder_url
                      ? <>Override actif sur ce profil. Cliquez sur « Edit » pour ajuster.</>
                      : <>Value inherited from the global setting (Settings → Freelance global Drive). Click 'Edit' to override.</>
                  }
                </p>
              </div>

              {/* Contract upload — only available after the profile is saved at least once */}
              {freelanceTarget?.is_freelance && (
                <div className="bg-brand-cream/40 border border-brand-navy/15 rounded-md p-4 space-y-2">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-brand-navy-500 font-mono">Signed contract</div>
                  {freelanceTarget.freelance_profile?.contract_drive_url ? (
                    <a href={freelanceTarget.freelance_profile.contract_drive_url} target="_blank" rel="noreferrer" className="text-sm text-brand-navy hover:underline" data-testid="fl-contract-link">
                      View le contrat actuel
                    </a>
                  ) : (
                    <p className="text-sm text-zinc-500">None contrat uploaded pour l'instant.</p>
                  )}
                  <label className="inline-flex items-center gap-2 px-3 h-9 mt-2 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600 cursor-pointer disabled:opacity-50">
                    <input type="file" accept="application/pdf" disabled={flUploading} onChange={(e) => uploadFreelanceContract(e.target.files?.[0])} data-testid="fl-contract-upload" className="hidden" />
                    <Upload size={12} /> {flUploading ? "Upload…" : "Uploader le contrat"}
                  </label>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex justify-between">
            {freelanceTarget?.is_freelance ? (
              <button onClick={removeFreelance} data-testid="fl-remove-btn" className="px-3 h-9 text-xs text-red-600 hover:bg-red-50 rounded-md">
                Désactiver le profil freelance
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button onClick={() => { setFreelanceTarget(null); setFlForm(null); }} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100">Cancel</button>
              <button onClick={saveFreelance} data-testid="fl-save-btn" className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm font-semibold rounded-md hover:bg-brand-navy-600">
                <CheckCircle2 size={14} /> Save
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inline new-portage dialog (opened from the freelance config dialog) */}
      <Dialog open={newPortageOpen} onOpenChange={setNewPortageOpen}>
        <DialogContent className="max-w-md" data-testid="new-portage-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau cabinet de portage</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Nom *</Label>
              <Input value={newPortage.name} onChange={(e) => setNewPortage({ ...newPortage, name: e.target.value })} placeholder="ex. UMAlis" data-testid="new-portage-name" />
            </div>
            <div>
              <Label>Address</Label>
              <Input value={newPortage.address} onChange={(e) => setNewPortage({ ...newPortage, address: e.target.value })} data-testid="new-portage-address" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Phone</Label>
                <Input value={newPortage.phone} onChange={(e) => setNewPortage({ ...newPortage, phone: e.target.value })} data-testid="new-portage-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={newPortage.email} onChange={(e) => setNewPortage({ ...newPortage, email: e.target.value })} data-testid="new-portage-email" />
              </div>
            </div>
            <div>
              <Label>ICE</Label>
              <Input value={newPortage.ice} onChange={(e) => setNewPortage({ ...newPortage, ice: e.target.value })} data-testid="new-portage-ice" />
            </div>
            {/* Iter-193 — optional banking details, collapsed by default. */}
            <div className="border-t border-zinc-100 pt-3">
              <button
                type="button"
                onClick={() => setNewPortageBankingOpen((v) => !v)}
                data-testid="new-portage-banking-toggle"
                className="w-full flex items-center justify-between text-sm font-medium text-zinc-700 hover:text-zinc-900"
              >
                <span>Coordonnées bancaires <span className="text-xs text-zinc-400 font-normal">(optionnel)</span></span>
                <span className="text-zinc-400 text-xs">{newPortageBankingOpen ? "−" : "+"}</span>
              </button>
              {newPortageBankingOpen && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="new-portage-banking-fields">
                  <div>
                    <Label>Banque</Label>
                    <Input value={newPortage.bank_name} onChange={(e) => setNewPortage({ ...newPortage, bank_name: e.target.value })} data-testid="new-portage-bank-name" placeholder="ex. Société Générale" />
                  </div>
                  <div>
                    <Label>SWIFT / BIC</Label>
                    <Input value={newPortage.swift} onChange={(e) => setNewPortage({ ...newPortage, swift: e.target.value })} data-testid="new-portage-swift" placeholder="ex. SOGEMAMC" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>IBAN</Label>
                    <Input value={newPortage.iban} onChange={(e) => setNewPortage({ ...newPortage, iban: e.target.value })} data-testid="new-portage-iban" placeholder="ex. MA64 022 …" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Domiciliation</Label>
                    <Input value={newPortage.domiciliation} onChange={(e) => setNewPortage({ ...newPortage, domiciliation: e.target.value })} data-testid="new-portage-domiciliation" placeholder="ex. Agence Casa-Maârif" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setNewPortageOpen(false)} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100">Cancel</button>
            <button onClick={createPortageInline} data-testid="new-portage-save" className="inline-flex items-center gap-2 px-4 h-10 bg-brand-navy text-white text-sm font-semibold rounded-md hover:bg-brand-navy-600">
              <CheckCircle2 size={14} /> Createdr
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CompensationDialog
        user={compensationTarget}
        open={!!compensationTarget}
        onOpenChange={(o) => { if (!o) setCompensationTarget(null); }}
        onSaved={load}
      />

      {/* Iter-231 — strong confirmation for permanent deletion (type the name). */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmName(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete permanently</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <p>
              This will <span className="font-semibold">permanently delete</span> the account of{" "}
              <span className="font-semibold">{deleteTarget?.name}</span> ({deleteTarget?.email}) and revoke all sessions and Google tokens.
              CRA history, mission assignments and expenses are kept for audit purposes.
            </p>
            <p className="text-zinc-600">
              A full data export (ZIP) is automatically archived to Google Drive before deletion. If the Drive archiving fails, the deletion is blocked.
            </p>
            <div>
              <Label>Type the user's full name to confirm</Label>
              <Input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={deleteTarget?.name || ""}
                data-testid="delete-confirm-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => { setDeleteTarget(null); setDeleteConfirmName(""); }} className="px-4 h-10 text-sm rounded-md hover:bg-zinc-100" data-testid="delete-cancel-btn">Cancel</button>
            <button
              onClick={deleteUser}
              disabled={deleting || (deleteConfirmName.trim().toLowerCase() !== (deleteTarget?.name || "").trim().toLowerCase())}
              data-testid="delete-confirm-btn"
              className="inline-flex items-center gap-2 px-4 h-10 bg-red-600 text-white text-sm font-semibold rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 size={14} /> {deleting ? "Deleting…" : "Delete permanently"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
